import { ComplexTable } from '@synergy/table';
import { Col, Row } from 'antd';
import clsx from 'clsx';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../../../../../contexts/AuthContext';
import query from '../../../../../server/constants.api';
import useFetch from "../../../../../server/useFetch";
import { PageContent, PageSection, PageTitle } from "../../../../../styles/global/Pages.styled";
import { AUTH_USER_POOL_ID } from '../../../../../utils/config/configs';
import Spinner from '../../../Atomics/Spinner';
import { Text } from '../../../Atomics/Text';
import permissionToEdit from '../AccessUtils';
import { getCustomizedButtonsForConfiguration } from '../ConfigurationUtils';
import UsersCreateEditModal from './UsersCreateEditModal/UsersCreateEditModal';

export const UsersConfigurations = ({ ...props }) => {
    // Obtém a função de tradução do hook useTranslation para uso na tradução de textos
    const allowedToWrite = permissionToEdit(['Configurations', 'UsersConfigurations'])
    const { t } = useTranslation();
    const { tenant } = useAuthContext();

    const [userTableList, setUserTableList] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [isToEdit, setIsToEdit] = useState(false);

    const {
        data: userList,
        isFetching: isFetchingUserList,
        refetch: refetchUserList
    } = useFetch("/users",
        "userList",
        {
            tenant: tenant,
            userPool: AUTH_USER_POOL_ID
        },
        null,
        null,
        query.middlewareURL);


    useEffect(() => {
        if (userList) {
            setUserTableList(userList.map((user, index) => {
                return {
                    ...user,
                    id: index,
                }
            }));
        }
    }, [userList]);

    const handleEdit = (record) => {
        setIsToEdit(true); // Ativa a flag de edição
        setSelectedRecord(record);  // Atualiza o estado com o registro selecionado
        setIsModalOpen(true); // Abre a modal
    }

    const handleCloseModal = () => {
        setSelectedRecord(null);
        setIsModalOpen(false);
    }

    const emptyRecord = {
        "username": null,
        "email": null,
        "tenant": null,
        "zones": null,
        "tags": null,
        "phone_number": null,
        "temporary_password": null,
        "mfa_active": false
    }

    const tableConfiguration = {
        collumnsConfiguration: [
            {
                title: t('Name'),
                dataIndex: 'username',
            },
            {
                title: t('Phone Number'),
                dataIndex: 'phone_number',
            },
            {
                title: t('Email'),
                dataIndex: 'email',
            },
            {
                title: t('Tenant'),
                dataIndex: 'tenant',
                tags: true,
            },
            {
                title: t('Zones'),
                dataIndex: 'zones',
                tags: true,
            },
            {
                title: t('Tags'),
                dataIndex: 'tags',
                tags: true,
            }
        ]
    }

    /**
     * Updates the user table list with new user data.
     * If the user already exists in the list, it updates the user's information.
     * If the user does not exist, it adds the new user to the list.
     *
     * @param {Object} newUserData - The new user data to be added or updated.
     * @param {string} newUserData.username - The username of the user.
     * @param {OtherType} newUserData.otherProperty - Other properties of the user (replace with actual properties).
     */
    const updateUserTableList = (newUserData) => {
        setUserTableList(prevList => {
            const existingUserIndex = prevList.findIndex(user => user.username === newUserData.username);
            if (existingUserIndex > -1) {
                return prevList.map((user, index) => {
                    if (index === existingUserIndex) {
                        return newUserData;
                    }
                    return user;
                });
            } else {
                return [...prevList, newUserData];
            }
        });
    }

    return (
        <PageContent className={clsx('building_room_details', props?.className)}>
            <PageSection>
                <Row gutter={[16, 16]}>
                    <Col span={24}>
                        <Row gutter={[16, 16]}>
                            <Col span={4}>
                                <PageTitle>
                                    <Text $fontColor='coolgray-12' $fontSize='text_md' $weight='medium' $marginRight='s03'>{t('Users List')}</Text>
                                </PageTitle>
                            </Col>
                        </Row>
                    </Col>
                    <Col span={24}>
                        {userList && !isFetchingUserList ?
                            <ComplexTable
                                fetchData={userTableList}
                                language={i18next.resolvedLanguage}
                                tableConfiguration={tableConfiguration}
                                editButton={allowedToWrite}
                                fixedActions={true}
                                handleEdit={(record) => { handleEdit({ ...emptyRecord, ...record }) }}
                                customizedButtons={
                                    getCustomizedButtonsForConfiguration(allowedToWrite,
                                        () => { setIsToEdit(false); setSelectedRecord(emptyRecord); setIsModalOpen(true) }, t)
                                }
                                pagination={{ pageSize: 5 }}
                            />
                            :
                            <div style={{ height: 'inherit', width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                <Spinner stroke="green" />
                            </div>
                        }
                    </Col>
                    <UsersCreateEditModal
                        isModalOpen={isModalOpen}
                        handleCloseModal={handleCloseModal}
                        isToEdit={isToEdit}
                        selectedRecord={selectedRecord}
                        updateUserTableList={updateUserTableList}
                    />
                </Row>
            </PageSection>
        </PageContent>
    )
}